import { useCallback, useEffect, useRef } from 'react';

/**
 * Returns a stable debounced version of the provided callback.
 * The debounced function delays invocation until `delay` ms have
 * elapsed since the last call. Pending calls are cancelled on unmount.
 *
 * @example
 * ```tsx
 * const debouncedSearch = useDebounceCallback((value: string) => {
 *   setSearchQuery(value);
 * }, 300);
 *
 * <input onChange={(e) => debouncedSearch(e.target.value)} />
 * ```
 */
function useDebounceCallback<TArgs extends unknown[]>(callback: (...args: TArgs) => void, delay: number) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const debouncedFn = useCallback(
    (...args: TArgs) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => callbackRef.current(...args), delay);
    },
    [delay],
  );

  const cancel = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return Object.assign(debouncedFn, { cancel });
}

export { useDebounceCallback };
